import React, { useState, useRef, useContext } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Auth } from 'aws-amplify';

import { makeStyles } from '@material-ui/core/styles';
import {
	AppBar,
	Toolbar,
	Grid,
	Button,
	List,
	ListItem,
	ListItemText,
	MenuList,
	MenuItem,
	ClickAwayListener,
	Popper,
	Container,
} from '@material-ui/core';

import {
	HOMEPAGE_PATH,
	PORTFOLIO_PATH,
	CONTACT_PATH,
	USER_PROFILE_PATH,
} from '../routes';
import PageRegister from '../pages/PageRegister';
import PageLogin from '../pages/PageLogin';

import { setJwt } from '../services/httpService';
import { UserContext } from '../context/UserState';
import theme from '../styles/theme/theme';

import IconChevron from '../assets/icon-chevron.svg';
import IconAccount from '../assets/icon-account.svg';

const useStyles = makeStyles(() => ({
	navPopper: {
		width: 210,
		padding: 0,
		marginTop: 10,
		zIndex: 9999,
		backgroundColor: theme.palette.blocks.header.dropdown.backgroundColor,
		borderRadius: 5,
		boxShadow:
			'0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)',

		'& .MuiList-padding': {
			padding: 0,
		},

		'& .MuiListItem-root': {
			display: 'block',
		},

		'& .MuiList-root > li': {
			padding: 0,

			'&:not(:first-child)': {
				borderTop: `1px solid ${theme.palette.blocks.header.dropdown.dividerColor}`,
			},

			'&:hover a': {
				color: theme.palette.blocks.header.dropdown.linkColorHover,
			},

			'&:hover button': {
				color: theme.palette.blocks.header.dropdown.linkColorHover,
			},
		},

		'& a': {
			color: theme.palette.blocks.header.dropdown.linkColor,
			textDecoration: 'none',
		},

		'& button': {
			width: '100%',
			padding: 0,
			backgroundColor: 'transparent',
			border: 0,
			textAlign: 'left',
			color: theme.palette.blocks.header.dropdown.linkColor,
			fontSize: 16,
			fontFamily: theme.typography.fontFamily,
			cursor: 'pointer',
		},

		'& a, & button': {
			padding: 20,
			display: 'block',
		},
	},
}));

/* eslint-disable indent */
const Root = styled.header`
	padding: 20px 0;
	background: ${(props) => props.theme.palette.blocks.header.background};
	color: ${(props) => props.theme.palette.blocks.header.color};

	.appbar {
		box-shadow: none;
	}

	.navigation {
		display: flex;
		align-items: center;
		justify-content: flex-end;

		.MuiListItem-root {
			display: inline-block;
			width: auto;
			margin: 0 5px;
			text-align: center;

			&:hover {
				span {
					color: ${(props) => props.theme.palette.text.link};
				}
			}

			&.Mui-selected,
			&.Mui-selected:hover {
				background-color: transparent;

				&::after {
					content: '';
					width: calc(100% - 20px);
					height: 3px;
					position: absolute;
					left: 10px;
					background-color: ${(props) =>
						props.theme.palette.blocks.header.borderColorActiveLink};
				}
			}
		}

		.MuiListItem-gutters {
			padding: 0 10px;
		}

		.MuiListItemText-root {
			width: auto;
			min-width: auto;
			margin: 0;
			flex: 0 1 auto;

			span {
				text-transform: uppercase;
				font-size: 14px;
				font-weight: bold;
				color: ${(props) => props.theme.palette.blocks.header.color};
			}
		}

		.MuiButtonBase-root {
			.MuiListItemText-root {
				span {
					text-transform: initial;
				}
			}
		}

		.navigation__btn {
			padding: 10px 40px;
			background-color: ${(props) =>
				props.theme.palette.button.outline.backgroundColor};
			color: ${(props) => props.theme.palette.button.outline.color};
			box-shadow: none;
			font-size: 18px;
			border-radius: 50px;
			border: 2px solid ${(props) => props.theme.palette.button.outline.color};
			transition: color 250ms, border-color 250ms, background-color 250ms;

			&:hover {
				background-color: ${(props) =>
					props.theme.palette.button.outline.backgroundColorHover};
				box-shadow: none;
				border: 2px solid white;

				span {
					color: ${(props) =>
						props.theme.palette.button.outline.colorHover} !important;
				}
			}
		}
	}

	.account {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-left: 10px;

		img {
			max-width: 40px;
			width: 100%;
			height: auto;
		}
	}

	.logo {
		a {
			display: block;
		}

		img {
			width: 100%;
			max-width: 190px;
		}
	}
`;

const NavDivider = styled.div`
	width: 1px;
	margin: 0 10px;
	background-color: #959595;
	height: 24px;
`;

const CustomNavDropDownArrow = styled.div`
	&::after {
		content: url(${IconChevron});
		display: inline-block;
		margin-left: 12px;
		width: 20px;
		vertical-align: 0;
		border-top: 0;
		border-right: 0;
		border-bottom: 0;
		border-left: 0.3em solid transparent;
	}
`;

const Header = () => {
	const classes = useStyles();
	const [open, setOpen] = useState(false);
	const { user, setUser } = useContext(UserContext);
	const [openLoginModal, setOpenLoginModal] = useState(false);
	const [openRegisterModal, setOpenRegisterModal] = useState(false);
	const history = useHistory();
	const anchorRef = useRef(null);
	const location = useLocation();
	const firstUrl = location.pathname.split('/')[1];

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}

		setOpen(false);
	};

	const handleSignOut = async () => {
		setJwt();
		try {
			await Auth.signOut();
			setUser({ isLoggedIn: false });
			setOpenLoginModal(false);
			history.push(`${HOMEPAGE_PATH}`);
		} catch (error) {
			console.log('error signing out: ', error);
		}
	};

	const NavbarPopper = (
		<Popper
			open={open}
			anchorEl={anchorRef.current}
			transition
			className={classes.navPopper}
		>
			<ClickAwayListener onClickAway={handleClose}>
				<MenuList>
					<MenuItem>
						<Link
							to={USER_PROFILE_PATH}
							onClick={(e) => {
								handleClose(e);
							}}
						>
							Kundenkonto
						</Link>
					</MenuItem>
					<MenuItem>
						<button
							type="button"
							onClick={(e) => {
								handleClose(e);
								handleSignOut(e);
							}}
						>
							Abmelden
						</button>
					</MenuItem>
				</MenuList>
			</ClickAwayListener>
		</Popper>
	);

	let LoggedInComponent = null;
	switch (user.isLoggedIn) {
		case true:
			LoggedInComponent = (
				<List component="nav" className="navigation">
					<ListItem
						component={Link}
						to={PORTFOLIO_PATH}
						selected={firstUrl === PORTFOLIO_PATH.split('/')[1]}
					>
						<ListItemText primary="Portfolio" />
					</ListItem>
					<ListItem
						component={Link}
						to={CONTACT_PATH}
						selected={firstUrl === CONTACT_PATH.split('/')[1]}
					>
						<ListItemText primary="Kontakt" />
					</ListItem>
					<NavDivider />
					<div className="account">
						<Link to={USER_PROFILE_PATH}>
							<img src={IconAccount} alt="Kundenkonto" />
						</Link>
						<Button
							size="medium"
							onClick={handleToggle}
							ref={anchorRef}
							aria-controls={open ? 'menu-list-grow' : undefined}
							aria-haspopup="true"
						>
							{user.username}
							<CustomNavDropDownArrow />
						</Button>
					</div>
					{NavbarPopper}
				</List>
			);
			break;

		case false:
			LoggedInComponent = (
				<List component="nav" className="navigation">
					<ListItem
						component={Link}
						to={HOMEPAGE_PATH}
						selected={firstUrl === HOMEPAGE_PATH.split('/')[1]}
					>
						<ListItemText primary="Startseite" />
					</ListItem>
					<ListItem
						component={Link}
						to={CONTACT_PATH}
						selected={firstUrl === CONTACT_PATH.split('/')[1]}
					>
						<ListItemText primary="Kontakt" />
					</ListItem>
					<NavDivider />
					<ListItem component={Button} onClick={() => setOpenLoginModal(true)}>
						<ListItemText primary="Anmelden" />
					</ListItem>
					<ListItem
						component={Button}
						className="navigation__btn"
						onClick={() => setOpenRegisterModal(true)}
					>
						<ListItemText primary="Registrieren" />
					</ListItem>
				</List>
			);
			break;
		default:
			break;
	}

	return (
		<Root>
			<Grid component="header">
				<Container maxWidth="lg">
					<AppBar position="static" className="appbar" color="transparent">
						<Toolbar>
							<Grid container justify="flex-end" alignItems="center">
								<Grid item className="logo">
									<Link to={PORTFOLIO_PATH}>
										<img
											src={theme.companyLogo}
											className="logo__img"
											alt="Intilion Logo"
										/>
									</Link>
								</Grid>
								<Grid item style={{ flex: 1 }}>
									{LoggedInComponent}
								</Grid>
							</Grid>
						</Toolbar>
					</AppBar>
					<PageLogin
						openModal={openLoginModal}
						closeModal={() => setOpenLoginModal(!openLoginModal)}
						openRegisterModal={() => setOpenRegisterModal(!openRegisterModal)}
					/>
					<PageRegister
						openModal={openRegisterModal}
						closeModal={() => setOpenRegisterModal(!openRegisterModal)}
						openLoginModal={() => setOpenLoginModal(!openLoginModal)}
					/>
				</Container>
			</Grid>
		</Root>
	);
};

export default Header;
